import axios from 'src/axios';
import {
  readTerminologyAvailability,
  terminologyAvailabilityFromError
} from 'src/util/terminologyAvailability';

/**
 * The disease search, as every disease picker asks it (TGCLOUD-1892).
 *
 * Six pickers each had their own copy of this: the pedigree's Diagnoses add,
 * the re-code dialog, Settings → Family History → Disease list, the PED
 * import, the pregnancy editor's fetus diagnoses and bulk edit. Each asked
 * for 9 rows and waited, on scroll, for a second page the backend never
 * sends (it ignores `page`, and `totalCount` is the rows it returned). Four of
 * them dropped the labels the backend sends with each row: a row reached only
 * through a curated bridge (`viaXref`), the phenotypes it falls back to when no
 * disease matched (`fellBackToHpo`), and, since this ticket, the Mondo concept
 * a row stands for and the name the words matched.
 *
 * The backend groups the rows by meaning: one row per disease, carrying its
 * code in each vocabulary that holds it (`codingSystems`, preferred first by
 * Settings → Terminology). Every code carries its OWN name, and that is the
 * name stored with it: picking ICD-10-CM Q99.2 from the "Fragile X syndrome"
 * row records "Fragile X chromosome".
 */

export const DISEASE_SEARCH_URL = '/terminology/network/search/disease';

// One page is all the backend sends, so this is what a picker shows.
export const DISEASE_SEARCH_LIMIT = 20;

export const DISEASE_OPTION_KIND = Object.freeze({
  disease: 'disease',
  // No disease vocabulary matched and the rows are HPO phenotype terms.
  phenotype: 'phenotype'
});

const keyOf = (name) =>
  String(name || '')
    .toLowerCase()
    .replace(/['’]s\b/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** Two names that differ only in case, punctuation or a possessive. */
export const sameDiseaseName = (a, b) => !!keyOf(a) && keyOf(a) === keyOf(b);

/**
 * One row of the response as a picker option.
 *
 * An older backend sends only {id, name}: it gets a single code of its own, so
 * every picker's selection runs one way.
 */
export function toDiseaseOption(row, { phenotypes = false } = {}) {
  const codingSystems = (
    Array.isArray(row.codingSystems) && row.codingSystems.length
      ? row.codingSystems
      : [{ id: row.id, source: row.source, label: row.source }]
  ).map((cs) => ({
    id: cs.id,
    source: cs.source,
    label: cs.label || cs.source,
    // Each code's own name, which is what is stored when it is chosen.
    name: cs.name || row.name,
    ...(typeof cs.isLeaf === 'boolean' ? { isLeaf: cs.isLeaf } : {})
  }));
  return {
    id: row.id,
    name: row.name,
    source: row.source,
    sources: row.sources?.length
      ? row.sources
      : codingSystems.map((cs) => cs.label),
    hasDuplicates: codingSystems.length > 1,
    codingSystems,
    kind: phenotypes
      ? DISEASE_OPTION_KIND.phenotype
      : DISEASE_OPTION_KIND.disease,
    viaXref: row.viaXref === true,
    viaMondo: row.viaMondo === true,
    mondo: row.mondo?.id ? { id: row.mondo.id, label: row.mondo.label } : null,
    matched: row.matched?.value ? row.matched : null,
    replaces: row.replaces || null
  };
}

/** A response body as options, with why the list may be short or empty. */
export function diseaseOptionsFrom(data) {
  const body = data && typeof data === 'object' ? data : {};
  const phenotypes = body.fellBackToHpo === true;
  return {
    options: (Array.isArray(body.results) ? body.results : [])
      .filter((r) => r?.id && r?.name)
      .map((r) => toDiseaseOption(r, { phenotypes })),
    unavailable: readTerminologyAvailability(body),
    fellBackToHpo: phenotypes
  };
}

/**
 * Ask the disease search. Never throws: a request that failed answers with
 * no options and says why, so an empty list never reads as "no matches"
 * (TGCLOUD-1785).
 */
export async function searchDiseases(q, { limit = DISEASE_SEARCH_LIMIT } = {}) {
  try {
    const { data } = await axios.get(DISEASE_SEARCH_URL, {
      params: { q, limit }
    });
    return diseaseOptionsFrom(data);
  } catch (err) {
    console.error('Disease search failed:', err);
    return {
      options: [],
      unavailable: terminologyAvailabilityFromError(),
      fellBackToHpo: false
    };
  }
}

/** Every code an option holds. */
export const codesOfOption = (option) =>
  new Set(
    (option?.codingSystems?.length ? option.codingSystems : [option])
      .map((cs) => cs?.id)
      .filter(Boolean)
  );

/**
 * The diagnosis already recorded that this option is, or null.
 *
 * By code: any of the row's codes already on the person is the same disease,
 * so ORPHA:908 is caught on top of OMIM:300624 when Mondo groups them. By name
 * too, as before, for a code the grouping does not reach.
 *
 * @param {object} option a disease option
 * @param {Array<{id, name}>} recorded what the person holds
 * @param {(a: string, b: string) => boolean} [sameId] how two ids compare
 */
export function recordedAs(option, recorded = [], sameId = (a, b) => a === b) {
  const codes = [...codesOfOption(option)];
  const names = [
    option?.name,
    ...(option?.codingSystems || []).map((cs) => cs.name)
  ];
  return (
    (recorded || []).find(
      (r) =>
        codes.some((c) => sameId(r?.id, c)) ||
        names.some((n) => sameDiseaseName(r?.name, n))
    ) || null
  );
}

/** What is stored for the code chosen from a row: that code, with its own name. */
export function storedDiseaseFor(option, codeId) {
  const cs = (option?.codingSystems || []).find((c) => c.id === codeId) || null;
  return {
    id: cs ? cs.id : option.id,
    name: cs?.name || option.name
  };
}
